import { Link } from "react-router-dom";
import { FOUNDATION_PILLARS, IMAGES } from "../lib/content";
import { Leaf, HeartHandshake, ArrowRight } from "lucide-react";

export default function Foundation() {
  return (
    <div data-testid="foundation-page" className="bg-cream">
      {/* Hero */}
      <section className="container-vk pt-20 md:pt-28 pb-20 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <p className="overline mb-6">The Delinam Foundation</p>
          <h1 className="heading-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight">
            A name that means{" "}
            <span className="italic text-burgundy">&ldquo;God is with us.&rdquo;</span>{" "}
            A mission that refuses to leave anyone behind.
          </h1>
          <p className="mt-8 text-lg text-ink/70 max-w-2xl leading-relaxed">
            Delinam is Victor&rsquo;s civic vehicle &mdash; founded on the conviction that
            climate change is, before anything else, a public-health emergency,
            and that the people closest to it must be the ones writing the response.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden border border-paperline">
            <img
              src={IMAGES.foundation}
              alt="Delinam Foundation community work"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Initiative banner */}
      <section
        data-testid="foundation-initiative"
        className="relative bg-[#0F172A] text-cream overflow-hidden py-24 md:py-32"
      >
        <div className="container-vk grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-2 hidden lg:block">
            <Leaf size={56} className="text-gold" />
          </div>
          <div className="lg:col-span-10">
            <p className="overline text-gold mb-4">The Flagship Initiative</p>
            <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Climate Change &amp; Health Advocacy &mdash; building the response
              from the ground that is already shifting.
            </h2>
            <p className="mt-6 text-cream/80 max-w-3xl leading-relaxed">
              Across coastal Ghana, the Volta belt and the inland highlands,
              climate change is producing measurable health burdens long before
              policy can keep up. Delinam works at that frontline &mdash; with
              clinics, scholarships and stories.
            </p>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="container-vk py-24 md:py-32" data-testid="foundation-pillars">
        <p className="overline mb-5">Three Pillars</p>
        <h2 className="heading-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05] max-w-3xl mb-16">
          The work has shape. So does the impact.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 stagger">
          {FOUNDATION_PILLARS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`pillar-${i}`}
              className="border border-paperline p-8 md:p-10 animate-fade-up hover:border-burgundy transition-colors bg-cream"
            >
              <p className="heading-display text-4xl font-semibold text-burgundy">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-6 heading-display text-2xl md:text-3xl font-semibold leading-tight">
                {p.title}
              </h3>
              <p className="mt-4 text-ink/75 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-cream-deep py-24 md:py-28" data-testid="foundation-cta">
        <div className="container-vk grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8 flex items-start gap-5">
            <HeartHandshake size={44} className="text-burgundy shrink-0 mt-2" />
            <div>
              <p className="overline mb-3">Get Involved</p>
              <h3 className="heading-display text-3xl md:text-4xl font-semibold leading-tight max-w-2xl">
                Partner with the Foundation, fund a fellowship, or bring our
                climate-health programme to your community.
              </h3>
            </div>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <Link
              to="/contact"
              data-testid="foundation-contact-cta"
              className="btn-primary"
            >
              Begin a Conversation <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
